import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';
import { UploadHandlers } from './types';
import { useDataFeedWizardStore } from '../../../../store/dataFeedWizard/dataFeedWizardStore';
import { DanelFileInfo } from '../../../DanelFileUploadItem/types';
import { FileInfo } from '../../../FileUpload/types';
import { BankType, UploadedFileInfo } from '../../../../types/generatedTypes';
import Actions from '../../../../store/Actions';
import {
  UPLOAD_TAB_VAULTS,
  MAX_FILE_SIZE_BYTES,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
} from './constants';
import { RowType } from '../../../../types/rowType';

// Retry helper function
const retryWithDelay = async <T>(
  fn: () => Promise<T>,
  retries: number = 1,
  delay: number = 1000
): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryWithDelay(fn, retries - 1, delay);
    }
    throw error;
  }
};

// Convert restored file metadata to display format
const convertRestoredToDisplayFile = (
  restoredFile: UploadedFileInfo
): DanelFileInfo => ({
  file: new File([], restoredFile.filename),
  id: `restored-${restoredFile.filename}`,
  name: restoredFile.filename,
  size: 'Previously uploaded',
  type: '',
  bank: restoredFile.bank_type as BankType | undefined,
  isDetected: true,
  isRestored: true,
});

export const useUploadFilesStep = (): UploadHandlers => {
  const [, setSearchParams] = useSearchParams();

  const {
    activeUploadTab,
    vaultFiles,
    excelFiles,
    restoredFiles,
    returnExcel,
    rowsType,
    setActiveUploadTab,
    addVaultFiles,
    addExcelFiles,
    setVaultFiles,
    setExcelFiles,
    removeVaultFile,
    removeExcelFile,
    updateVaultFileBank,
    setReturnExcel,
    setRowsType,
    setCurrentStepButtonState,
    registerStepActions,
    clearStepActions,
    setSessionId,
    setFileId,
    sessionId,
    sessionName,
    sessionNameValidation,
    setSessionName,
    setSessionNameValidation,
  } = useDataFeedWizardStore();

  // Track original session name for restored sessions
  const originalSessionNameRef = useRef<string | null>(null);

  const [isDetecting, setIsDetecting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [localSessionName, setLocalSessionName] = useState(sessionName);

  // Combined arrays: new files + restored files for unified rendering
  const allVaultFiles = useMemo(
    () => [
      ...vaultFiles,
      ...restoredFiles
        .filter(f => f.upload_tab === 'vaults')
        .map(convertRestoredToDisplayFile),
    ],
    [vaultFiles, restoredFiles]
  );

  const allExcelFiles = useMemo(
    () => [
      ...excelFiles,
      ...restoredFiles
        .filter(f => f.upload_tab === 'excel')
        .map(convertRestoredToDisplayFile),
    ],
    [excelFiles, restoredFiles]
  );

  const isRestoredSession =
    sessionId !== null ||
    (restoredFiles.length > 0 &&
      vaultFiles.length === 0 &&
      excelFiles.length === 0);

  // Port bank detection logic from useDanelFileUploadModal
  const detectFiles = useCallback(
    async (filesToDetect: DanelFileInfo[]): Promise<DanelFileInfo[]> => {
      setIsDetecting(true);
      try {
        const fileObjects = filesToDetect.map(f => f.file);
        const detectionResponse = await Actions.detectVaultTypes(fileObjects);

        // Map detection results back to files
        const updatedFiles = filesToDetect.map(danelFile => {
          const result = detectionResponse.results.find(
            r => r.filename === danelFile.name
          );

          if (!result) {
            return danelFile;
          }

          const detectedTypes = result.detected_types as BankType[];

          if (detectedTypes.length === 0) {
            // No detection
            return {
              ...danelFile,
              isDetected: false,
              detectedBanks: [],
              isAmbiguous: false,
              bank: undefined,
            };
          } else if (detectedTypes.length === 1) {
            // Single detection - set bank automatically
            return {
              ...danelFile,
              isDetected: true,
              detectedBanks: detectedTypes,
              isAmbiguous: false,
              bank: detectedTypes[0],
            };
          } else {
            // Multiple detections - ambiguous
            return {
              ...danelFile,
              isDetected: true,
              detectedBanks: detectedTypes,
              isAmbiguous: true,
              bank: undefined,
            };
          }
        });

        return updatedFiles;
      } catch (error) {
        console.error(ERROR_MESSAGES.DETECTION_FAILED, error);
        return filesToDetect;
      } finally {
        setIsDetecting(false);
      }
    },
    []
  );

  const handleVaultFilesSelect = useCallback(
    async (selectedFiles: FileInfo[]): Promise<void> => {
      // Validate file sizes
      const oversizedFiles = selectedFiles.filter(
        file => file.file.size > MAX_FILE_SIZE_BYTES
      );

      if (oversizedFiles.length > 0) {
        oversizedFiles.forEach(file => {
          console.error(ERROR_MESSAGES.FILE_TOO_LARGE(file.name));
        });
        return;
      }

      const danelFiles: DanelFileInfo[] = selectedFiles.map(file => ({
        ...file,
        bank: undefined,
      }));

      // Detect vault types for selected files
      const detectedFiles = await detectFiles(danelFiles);
      addVaultFiles(detectedFiles);

      // Ensure auto-detected banks are explicitly saved to store
      detectedFiles.forEach((file, index) => {
        if (file.bank) {
          const actualIndex = vaultFiles.length + index;
          updateVaultFileBank(actualIndex, file.bank);
        }
      });
    },
    [detectFiles, addVaultFiles, vaultFiles.length, updateVaultFileBank]
  );

  const handleExcelFilesSelect = useCallback(
    (selectedFiles: FileInfo[]): void => {
      // Validate file sizes
      const oversizedFiles = selectedFiles.filter(
        file => file.file.size > MAX_FILE_SIZE_BYTES
      );

      if (oversizedFiles.length > 0) {
        oversizedFiles.forEach(file => {
          console.error(ERROR_MESSAGES.FILE_TOO_LARGE(file.name));
        });
        return;
      }

      const danelFiles: DanelFileInfo[] = selectedFiles.map(file => ({
        ...file,
        bank: undefined,
      }));
      addExcelFiles(danelFiles);
    },
    [addExcelFiles]
  );

  const handleVaultBankChange = useCallback(
    (index: number, bank: BankType): void => {
      updateVaultFileBank(index, bank);
    },
    [updateVaultFileBank]
  );

  const handleRemoveVaultFile = useCallback(
    (index: number): void => {
      removeVaultFile(index);
    },
    [removeVaultFile]
  );

  const handleRemoveExcelFile = useCallback(
    (index: number): void => {
      removeExcelFile(index);
    },
    [removeExcelFile]
  );

  const handleTabChange = (
    _: React.SyntheticEvent,
    newValue: 'vaults' | 'excel'
  ): void => {
    setActiveUploadTab(newValue);
  };

  const handleReturnExcelChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setReturnExcel(event.target.checked);
    },
    [setReturnExcel]
  );

  const handlePositionsUploadChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setRowsType(event.target.checked ? RowType.ROW_TYPE_POSITION : RowType.ROW_TYPE_TRANSACTION);
    },
    [setRowsType]
  );

  // Debounced session name validation
  const validateSessionNameUniqueness = useMemo(
    () =>
      debounce(async (name: string) => {
        if (name.trim().length < 3) {
          setSessionNameValidation({
            isChecking: false,
            isUnique: null,
            error: null,
          });
          return;
        }

        setSessionNameValidation({ isChecking: true, error: null });
        try {
          const response = await Actions.checkWizardSessionNameAvailable(
            name.trim(),
            sessionId || undefined
          );
          setSessionNameValidation({
            isChecking: false,
            isUnique: response.available,
            error: response.available ? null : 'Flow name already exists',
          });
        } catch {
          setSessionNameValidation({
            isChecking: false,
            isUnique: null,
            error: 'Failed to validate flow name',
          });
        }
      }, 800),
    [sessionId, setSessionNameValidation]
  );

  // Debounce store update to reduce re-renders while typing
  const debouncedSetSessionName = useMemo(
    () => debounce((name: string) => setSessionName(name), 300),
    [setSessionName]
  );

  const handleSessionNameChange = useCallback(
    (name: string): void => {
      const capitalizedName = name.charAt(0).toUpperCase() + name.slice(1);
      setLocalSessionName(capitalizedName);
      debouncedSetSessionName(capitalizedName);
      validateSessionNameUniqueness(name); // Already debounced at 800ms
    },
    [debouncedSetSessionName, validateSessionNameUniqueness]
  );

  // Store original session name when session is restored
  useEffect(() => {
    if (sessionId && sessionName && originalSessionNameRef.current === null) {
      originalSessionNameRef.current = sessionName;
    }
  }, [sessionId, sessionName]);

  // Sync local state when store changes externally (for restore scenarios)
  useEffect(() => {
    setLocalSessionName(sessionName);
  }, [sessionName]);

  const handleUpload = useCallback(async (): Promise<boolean> => {
    // If session already exists, update name if changed then proceed
    if (sessionId) {
      // Check if session name was changed for restored sessions
      if (
        originalSessionNameRef.current !== null &&
        sessionName !== originalSessionNameRef.current
      ) {
        try {
          await Actions.updateWizardSessionName(sessionId, sessionName.trim());
          originalSessionNameRef.current = sessionName;
        } catch (error) {
          console.error('Failed to update session name:', error);
          return false;
        }
      }
      return true;
    }

    setIsUploading(true);
    try {
      if (activeUploadTab === UPLOAD_TAB_VAULTS) {
        // Prepare files for parsing
        const filesToParse = vaultFiles.map(f => ({
          file: f.file,
          bank: f.bank!,
          name: f.name,
        }));

        // Parse vault files with retry
        const parseResponse = await retryWithDelay(() =>
          Actions.parseVaultFiles(filesToParse)
        );

        // Check for successful parses
        const successfulResults = parseResponse.results.filter(r => r.success);

        if (successfulResults.length === 0) {
          console.error(ERROR_MESSAGES.PARSE_ALL_FAILED);
          return false;
        }

        // Prepare parsed data for saving
        const parsedDataToSave = successfulResults.map(r => ({
          success: true,
          filename: r.filename,
          bank_type: r.bank_type,
          row_count: r.row_count || 0,
          parsed_data: r.parsed_data,
          error: null,
        }));

        // Save to database - response includes file_id
        const saveResponse = await retryWithDelay(() =>
          Actions.saveParsedVaults(parsedDataToSave, returnExcel, rowsType)
        );

        // Type guard: ensure we have VaultProcessResponse (not blob download response)
        if (!('file_id' in saveResponse) || !saveResponse.file_id) {
          throw new Error(ERROR_MESSAGES.MISSING_FILE_ID);
        }

        const fileId = saveResponse.file_id;
        const positionsFileId = saveResponse.positions_file_id ?? undefined;

        // Store the file_id
        setFileId(fileId);

        // Build a lookup map from original filename to output_filename
        const outputFilenameMap = new Map<string, string>();
        successfulResults.forEach(result => {
          if (result.parsed_data?.output_filename) {
            outputFilenameMap.set(
              result.filename,
              result.parsed_data.output_filename as string
            );
          }
        });

        // Prepare uploaded files metadata for session
        const uploadedFilesMetadata = vaultFiles.map(f => ({
          filename: f.name,
          output_filename: outputFilenameMap.get(f.name) ?? null,
          bank_type: f.bank!,
          upload_tab: 'vaults' as const,
        }));

        // Create wizard session explicitly with file metadata and name
        const sessionResponse = await retryWithDelay(() =>
          Actions.createWizardSession(
            fileId,
            uploadedFilesMetadata,
            sessionName.trim(),
            rowsType,
            positionsFileId
          )
        );

        // Check if session creation succeeded - throw error instead of fallback
        if (!sessionResponse.session_id) {
          throw new Error(ERROR_MESSAGES.MISSING_SESSION_ID);
        }

        // Store the session_id and update URL for refresh persistence
        setSessionId(sessionResponse.session_id);
        setSearchParams(
          { session_id: sessionResponse.session_id },
          { replace: true }
        );

        // Log success message
        const failedCount =
          parseResponse.results.length - successfulResults.length;
        if (failedCount > 0) {
          console.warn(
            SUCCESS_MESSAGES.PARTIAL_SUCCESS(
              successfulResults.length,
              failedCount
            )
          );
        } else {
          console.log(
            SUCCESS_MESSAGES.UPLOAD_VAULT_SUCCESS(successfulResults.length)
          );
        }

        // Clear excel files since we're proceeding with vault upload
        setExcelFiles([]);

        return true;
      } else {
        // Excel tab
        if (excelFiles.length === 0) {
          console.error(ERROR_MESSAGES.NO_EXCEL_FILES);
          return false;
        }

        // Upload Excel file with retry
        const file = excelFiles[0].file;
        const uploadResponse = await retryWithDelay(() =>
          Actions.uploadDanelExcel(file)
        );

        // Check if file_id exists - throw error instead of fallback
        if (!uploadResponse.file_id) {
          throw new Error(ERROR_MESSAGES.MISSING_FILE_ID);
        }

        const excelFileId = uploadResponse.file_id;

        // Prepare uploaded files metadata for Excel
        const uploadedFilesMetadata = excelFiles.map(f => ({
          filename: f.name,
          output_filename: null, // Excel files don't have generated filename
          bank_type: null, // Excel files don't have bank type
          upload_tab: 'excel' as const,
        }));

        // Create wizard session with retry, file metadata and name
        // Excel files always use ROW_TYPE_TRANSACTION
        const sessionResponse = await retryWithDelay(() =>
          Actions.createWizardSession(
            excelFileId,
            uploadedFilesMetadata,
            sessionName.trim(),
            RowType.ROW_TYPE_TRANSACTION
          )
        );

        // Check if session creation succeeded - throw error instead of fallback
        if (!sessionResponse.session_id) {
          throw new Error(ERROR_MESSAGES.MISSING_SESSION_ID);
        }

        setSessionId(sessionResponse.session_id);
        setSearchParams(
          { session_id: sessionResponse.session_id },
          { replace: true }
        );
        setFileId(excelFileId);

        console.log(SUCCESS_MESSAGES.UPLOAD_EXCEL_SUCCESS);

        // Clear vault files since we're proceeding with excel upload
        setVaultFiles([]);

        return true;
      }
    } catch (error) {
      console.error(
        error instanceof Error
          ? `${ERROR_MESSAGES.UPLOAD_FAILED}: ${error.message}`
          : ERROR_MESSAGES.UPLOAD_FAILED
      );
      return false;
    } finally {
      setIsUploading(false);
    }
  }, [
    activeUploadTab,
    vaultFiles,
    excelFiles,
    sessionId,
    returnExcel,
    rowsType,
    sessionName,
    setSessionId,
    setFileId,
  ]);

  useEffect(() => {
    let isValid = false;
    let validationMessage: string | undefined;

    // Session name validation
    const isSessionNameValid = sessionName.trim().length >= 3;
    const isSessionNameUnique = sessionNameValidation.isUnique === true;
    const isSessionNameChecking = sessionNameValidation.isChecking;

    // If we have restored files from a session, validation passes (session already exists)
    const hasRestoredFilesForValidation =
      restoredFiles.length > 0 &&
      vaultFiles.length === 0 &&
      excelFiles.length === 0;

    if (hasRestoredFilesForValidation && sessionId) {
      // Session exists with previously uploaded files - check session name is still valid
      isValid =
        isSessionNameValid && isSessionNameUnique && !isSessionNameChecking;
      if (!isValid) {
        validationMessage = !isSessionNameValid
          ? 'Flow name must be at least 3 characters'
          : isSessionNameChecking
            ? 'Checking flow name availability...'
            : sessionNameValidation.error || 'Flow name already exists';
      }
    } else if (activeUploadTab === UPLOAD_TAB_VAULTS) {
      const hasVaultFiles = vaultFiles.length > 0;
      const allVaultsMapped = vaultFiles.every(file => file.bank !== undefined);
      const filesValid = hasVaultFiles && allVaultsMapped;
      isValid =
        filesValid &&
        isSessionNameValid &&
        isSessionNameUnique &&
        !isSessionNameChecking;

      if (!isValid) {
        if (!isSessionNameValid) {
          validationMessage = 'Flow name must be at least 3 characters';
        } else if (isSessionNameChecking) {
          validationMessage = 'Checking flow name availability...';
        } else if (!isSessionNameUnique) {
          validationMessage =
            sessionNameValidation.error || 'Flow name already exists';
        } else if (!hasVaultFiles) {
          validationMessage = ERROR_MESSAGES.NO_VAULT_FILES;
        } else {
          validationMessage = ERROR_MESSAGES.UNMAPPED_BANKS;
        }
      }
    } else {
      const filesValid = excelFiles.length > 0;
      isValid =
        filesValid &&
        isSessionNameValid &&
        isSessionNameUnique &&
        !isSessionNameChecking;

      if (!isValid) {
        if (!isSessionNameValid) {
          validationMessage = 'Flow name must be at least 3 characters';
        } else if (isSessionNameChecking) {
          validationMessage = 'Checking flow name availability...';
        } else if (!isSessionNameUnique) {
          validationMessage =
            sessionNameValidation.error || 'Flow name already exists';
        } else {
          validationMessage = ERROR_MESSAGES.NO_EXCEL_FILES;
        }
      }
    }

    setCurrentStepButtonState({
      disabled: !isValid,
      loading: isUploading,
      validationMessage,
    });
    // Note: setCurrentStepButtonState is omitted from deps - Zustand store functions are stable
  }, [
    activeUploadTab,
    vaultFiles,
    excelFiles,
    isUploading,
    restoredFiles,
    sessionId,
    sessionName,
    sessionNameValidation,
  ]);

  // Register step actions when component mounts
  // Note: registerStepActions and clearStepActions are omitted from deps - Zustand store functions are stable
  useEffect(() => {
    registerStepActions({
      onNext: handleUpload,
    });

    return () => {
      clearStepActions();
    };
  }, [handleUpload]);

  // Computed: check if we have restored files (after page refresh)
  const hasRestoredFiles =
    restoredFiles.length > 0 &&
    vaultFiles.length === 0 &&
    excelFiles.length === 0;

  return {
    activeUploadTab,
    vaultFiles,
    excelFiles,
    restoredFiles,
    returnExcel,
    isDetecting,
    isUploading,
    hasRestoredFiles,
    allVaultFiles,
    allExcelFiles,
    isRestoredSession,
    sessionName: localSessionName,
    sessionNameValidation,
    handleTabChange,
    handleVaultFilesSelect,
    handleVaultBankChange,
    handleRemoveVaultFile,
    handleExcelFilesSelect,
    handleRemoveExcelFile,
    handleReturnExcelChange,
    rowsType,
    handlePositionsUploadChange,
    handleSessionNameChange,
    handleUpload,
  };
};
